import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import SearchIcon from '@mui/icons-material/Search';
import ResponsiveOverlay from '@/components/ResponsiveOverlay';
import PlaceSearchResults from '@/components/PlaceSearchAutocomplete/PlaceSearchResults';
import CustomImage from '@/components/CustomImage/CustomImage';
import RecommendThisPlaceModal from '@/components/RecommendThisPlaceModal/RecommendThisPlaceModal';
import { fetchAllUserPosts } from '@/api/auth';
import {
  listMyRecommendations,
  type EnrichedRecommendationDoc,
  type EnrichedRecommendationItem,
} from '@/api/enrichedRecommendation';
import { generateShareLink } from '@/api/share';
import { useAuth } from '@/contexts';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_LINK_PERFORMANCE_CREATE_OPENED,
  EVENT_LINK_PERFORMANCE_LINK_COPIED,
  EVENT_LINK_PERFORMANCE_LINK_CREATED,
  getShareContentType,
} from '@/lib/analyticsEvents';
import { error as logError } from '@/utils/logger';
import { toast } from 'react-toastify';
import { resolveLocationIdForSave } from '@/utils/resolveLocationIdForSave';
import { shareUrlViaPlatform } from '@/utils/editorMobileShare';
import { copyToClipboard } from '@/utils/shareUtils';
import { isNativePlatform } from '@/lib/platform';
import { isMongoObjectIdString } from '@/utils/idGeneration';
import Post, { PostType } from '@/models/post';
import Location from '@/models/location';
import { CREATE_LINK_COPY } from './createNewLinkCopy';
import type {
  CreateLinkStep,
  CreateLinkSuccessState,
  PickableResource,
  PickerTab,
} from './createNewLinkTypes';
import {
  enrichedLocationToLocation,
  getCreateLinkCategoryLabel,
  getCreateLinkPlacePhoto,
  getCreateLinkPlaceCity,
} from './createNewLinkUtils';
import { useCreateLinkPlaceSearch } from './useCreateLinkPlaceSearch';
import CreateNewLinkPlaceCard from './CreateNewLinkPlaceCard';
import './CreateLinkPickerSheet.css';

export type { PickableResource } from './createNewLinkTypes';

interface CreateNewLinkFlowProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Refresh-only hook after a share URL is generated (e.g. refetch the
   * performance list). Must not close the flow — the success screen still
   * needs to show Copy Link / Done.
   */
  onLinkCreated?: () => void;
}

function postKindLabel(post: Post): string {
  switch (post.post_type) {
    case PostType.Itinerary:
      return 'Trip';
    case PostType.List:
      return 'List';
    case PostType.Place:
      return 'Place';
    default:
      return post.post_type ?? 'Post';
  }
}

function skipsRecommendStep(resource: PickableResource): boolean {
  return resource.resource_type === 'post' || resource.resource_type === 'profile';
}

/** Ensure Location has a Mongo `_id` so RecommendThisPlaceModal can submit. */
async function withResolvedLocationId(location: Location): Promise<Location | null> {
  if (isMongoObjectIdString(location._id)) return location;

  const resolved = await resolveLocationIdForSave({
    locationId: location._id,
    googlePlaceId: location.google_place_id,
    name: location.name,
    address: location.address,
  });
  if (!resolved) return null;

  return new Location({
    _id: resolved.locationId,
    name: location.name,
    google_place_id: location.google_place_id,
    a3_intl_address: location.a3_intl_address,
    category_name: location.category_name,
    main_photo: location.main_photo,
    photos: location.photos,
    google_places_data: location.google_places_data,
    gt_category: (location as Location & { gt_category?: string }).gt_category,
  });
}

/** Inline content loader — the shared Spinner is a full-screen splash. */
const PickerLoader: React.FC<{ label: string }> = ({ label }) => (
  <div className="create-link-picker-loading" role="status" aria-live="polite">
    <div
      className="size-8 animate-spin rounded-full border-[3px] border-gray-200 border-t-orange-500"
      aria-hidden
    />
    <span className="mt-3">{label}</span>
  </div>
);

const CreateNewLinkFlow: React.FC<CreateNewLinkFlowProps> = ({
  isOpen,
  onClose,
  onLinkCreated,
}) => {
  const { user } = useAuth();
  const isMobileViewport = useIsMobileViewport();
  const nativeShareTriggeredRef = useRef(false);

  const [step, setStep] = useState<CreateLinkStep>('pick');
  const [tab, setTab] = useState<PickerTab>('explore');
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isOpeningRecommend, setIsOpeningRecommend] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [recommendations, setRecommendations] = useState<EnrichedRecommendationItem[]>([]);
  /** Place path: shared RecommendThisPlaceModal (same as pancake-menu Recommend a Place). */
  const [recommendLocation, setRecommendLocation] = useState<Location | null>(null);
  const [success, setSuccess] = useState<CreateLinkSuccessState | null>(null);

  const {
    query: searchQuery,
    handleQueryChange: handleSearchQueryChange,
    results: searchResults,
    isSearching,
    searchError,
    searchAttempted,
    resetSearch,
    retrySearch,
  } = useCreateLinkPlaceSearch();

  const resetFlow = useCallback(() => {
    setStep('pick');
    setTab('explore');
    setRecommendLocation(null);
    setSuccess(null);
    setIsGenerating(false);
    setIsOpeningRecommend(false);
    nativeShareTriggeredRef.current = false;
    resetSearch();
  }, [resetSearch]);

  useEffect(() => {
    if (!isOpen) {
      resetFlow();
      return;
    }
    WebAnalytics.track(EVENT_LINK_PERFORMANCE_CREATE_OPENED, {});
  }, [isOpen, resetFlow]);

  useEffect(() => {
    if (!isOpen || !user?._id) return;

    let cancelled = false;
    setIsLoading(true);

    const load = async () => {
      try {
        const [postsResult, recsResult] = await Promise.all([
          fetchAllUserPosts(user._id, { allowPrivate: true, allowDrafts: true }),
          listMyRecommendations({ page: 1, limit: 100 }),
        ]);

        if (cancelled) return;
        setPosts(postsResult.posts);
        setRecommendations(recsResult.items ?? []);
      } catch (err) {
        logError('[CreateNewLinkFlow] Failed to load resources', err);
        if (!cancelled) {
          toast.error('Unable to load your content. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, user?._id]);

  const profileResource = useMemo<PickableResource | null>(() => {
    if (!user?._id) return null;
    return {
      resource_type: 'profile',
      resource_id: user._id,
      title: user.name ? `${user.name}'s Profile` : 'My Profile',
      subtitle: 'Profile link',
    };
  }, [user?._id, user?.name]);

  const postResources = useMemo<PickableResource[]>(
    () =>
      posts.map((post) => ({
        resource_type: 'post' as const,
        resource_id: post._id,
        title: post.name ?? 'Untitled',
        subtitle: postKindLabel(post),
        content_type: getShareContentType(post.post_type),
      })),
    [posts],
  );

  const recommendationResources = useMemo(() => {
    const out: Array<{
      resource: PickableResource;
      location: Location | null;
    }> = [];
    for (const item of recommendations) {
      out.push({
        resource: {
          resource_type: 'recommendation',
          resource_id: item.recommendation._id,
          title: item.location?.name ?? 'Recommendation',
          subtitle: 'Recommendation',
        },
        location: enrichedLocationToLocation(item.location),
      });
    }
    return out;
  }, [recommendations]);

  const locationResources = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{
      resource: PickableResource;
      location: Location | null;
    }> = [];
    for (const item of recommendations) {
      const locationId = item.recommendation.location_id;
      if (!locationId || seen.has(locationId)) continue;
      seen.add(locationId);
      out.push({
        resource: {
          resource_type: 'location',
          resource_id: locationId,
          title: item.location?.name ?? 'Place',
          subtitle: 'Location',
        },
        location: enrichedLocationToLocation(item.location),
      });
    }
    return out;
  }, [recommendations]);

  const popularPlaces = useMemo(() => {
    const seen = new Set<string>();
    const out: Location[] = [];
    for (const item of recommendations) {
      const loc = enrichedLocationToLocation(item.location);
      if (!loc?._id || seen.has(loc._id)) continue;
      seen.add(loc._id);
      out.push(loc);
      if (out.length >= 8) break;
    }
    return out;
  }, [recommendations]);

  const visibleListResources = useMemo(() => {
    switch (tab) {
      case 'posts':
        return postResources.map((resource) => ({ resource, location: null as Location | null }));
      case 'recommendations':
        return recommendationResources;
      case 'profile':
        return profileResource
          ? [{ resource: profileResource, location: null as Location | null }]
          : [];
      case 'locations':
        return locationResources;
      default:
        return [];
    }
  }, [tab, postResources, recommendationResources, profileResource, locationResources]);

  const triggerNativeShareIfMobile = useCallback(
    async (shareUrl: string) => {
      if (!isMobileViewport || nativeShareTriggeredRef.current) return;
      const canShareNatively =
        isNativePlatform() ||
        (typeof navigator !== 'undefined' && typeof navigator.share === 'function');
      if (!canShareNatively) return;

      nativeShareTriggeredRef.current = true;
      try {
        await shareUrlViaPlatform(shareUrl);
      } catch (err) {
        const e = err as { name?: string };
        if (e?.name !== 'AbortError') {
          logError('[CreateNewLinkFlow] native share failed', err);
        }
      }
    },
    [isMobileViewport],
  );

  const finishWithSuccess = useCallback(
    async (state: CreateLinkSuccessState) => {
      setSuccess(state);
      setStep('success');
      onLinkCreated?.();
      await triggerNativeShareIfMobile(state.shareUrl);
    },
    [onLinkCreated, triggerNativeShareIfMobile],
  );

  const generateDirectLink = useCallback(
    async (resource: PickableResource, location: Location | null = null) => {
      setIsGenerating(true);
      try {
        const response = await generateShareLink({
          resource_type: resource.resource_type,
          resource_id: resource.resource_id,
        });
        WebAnalytics.track(EVENT_LINK_PERFORMANCE_LINK_CREATED, {
          resource_type: resource.resource_type,
          resource_id: resource.resource_id,
        });
        await finishWithSuccess({
          shareUrl: response.share_url,
          resourceType: resource.resource_type,
          resourceId: resource.resource_id,
          title: resource.title,
          location,
          categoryLabel: location ? getCreateLinkCategoryLabel(location) : null,
        });
      } catch (err) {
        logError('[CreateNewLinkFlow] generateShareLink failed', err);
        toast.error('Unable to create link. Please try again.');
      } finally {
        setIsGenerating(false);
      }
    },
    [finishWithSuccess],
  );

  const openRecommendModal = useCallback(async (location: Location) => {
    setIsOpeningRecommend(true);
    try {
      const resolved = await withResolvedLocationId(location);
      if (!resolved) {
        toast.error('Unable to resolve this place. Please try again.');
        return;
      }
      setRecommendLocation(resolved);
      setStep('recommend');
    } catch (err) {
      logError('[CreateNewLinkFlow] resolve location failed', err);
      toast.error('Unable to open recommendation. Please try again.');
    } finally {
      setIsOpeningRecommend(false);
    }
  }, []);

  const handlePlaceSelected = useCallback(
    (location: Location) => {
      void openRecommendModal(location);
    },
    [openRecommendModal],
  );

  const handleResourceSelected = useCallback(
    async (resource: PickableResource, location: Location | null) => {
      if (skipsRecommendStep(resource)) {
        await generateDirectLink(resource, location);
        return;
      }
      if (!location) {
        toast.error('Place details are unavailable. Please pick another item.');
        return;
      }
      await openRecommendModal(location);
    },
    [generateDirectLink, openRecommendModal],
  );

  const handleRecommended = useCallback(
    async (rec: EnrichedRecommendationDoc) => {
      const location = recommendLocation;
      setIsGenerating(true);
      try {
        const response = await generateShareLink({
          resource_type: 'recommendation',
          resource_id: rec._id,
        });
        WebAnalytics.track(EVENT_LINK_PERFORMANCE_LINK_CREATED, {
          resource_type: 'recommendation',
          resource_id: rec._id,
        });
        setRecommendLocation(null);
        await finishWithSuccess({
          shareUrl: response.share_url,
          resourceType: 'recommendation',
          resourceId: rec._id,
          title: location?.name ?? 'Place',
          location,
          categoryLabel: location ? getCreateLinkCategoryLabel(location) : null,
        });
      } catch (err) {
        logError('[CreateNewLinkFlow] generateShareLink after recommend failed', err);
        toast.error('Unable to create link. Please try again.');
        // Stay on / return to pick so the user can retry
        setRecommendLocation(null);
        setStep('pick');
      } finally {
        setIsGenerating(false);
      }
    },
    [recommendLocation, finishWithSuccess],
  );

  const handleRecommendModalClose = useCallback(() => {
    setRecommendLocation(null);
    setStep('pick');
  }, []);

  const handleCopyLink = useCallback(async () => {
    if (!success) return;
    try {
      await copyToClipboard(success.shareUrl);
      toast.success('Link copied');
      WebAnalytics.track(EVENT_LINK_PERFORMANCE_LINK_COPIED, {
        resource_id: success.resourceId,
      });
    } catch {
      toast.error('Unable to copy link');
    }
  }, [success]);

  const handleDone = useCallback(() => {
    onClose();
  }, [onClose]);

  const tabs: { id: PickerTab; label: string }[] = [
    { id: 'explore', label: CREATE_LINK_COPY.exploreTab },
    { id: 'posts', label: CREATE_LINK_COPY.postsTab },
    { id: 'recommendations', label: CREATE_LINK_COPY.recsTab },
    { id: 'locations', label: CREATE_LINK_COPY.placesTab },
    { id: 'profile', label: CREATE_LINK_COPY.profileTab },
  ];

  if (!isOpen) return null;

  // Same pattern as RecommendFromPostsFlowModal: swap pick overlay for the shared recommend modal.
  if (step === 'recommend' && recommendLocation) {
    return (
      <RecommendThisPlaceModal
        isOpen
        onClose={handleRecommendModalClose}
        mode="create"
        location={recommendLocation}
        skipSaveOverlay
        submitLabel={CREATE_LINK_COPY.generateLink}
        onRecommended={(rec) => {
          void handleRecommended(rec);
        }}
      />
    );
  }

  const headerTitle =
    step === 'success' ? CREATE_LINK_COPY.step3Title : CREATE_LINK_COPY.step1Title;

  const headerBody =
    step === 'success' ? CREATE_LINK_COPY.step3Body : CREATE_LINK_COPY.step1Body;

  const flowContent = (
    <>
      <div className="create-link-picker-header">
        <div className="min-w-0 flex-1">
          <h2 className="create-link-picker-title font-lora tracking-wide">{headerTitle}</h2>
          <p className="mt-1 font-manrope text-footnote leading-4 text-gray-500">{headerBody}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="create-link-picker-close"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {step === 'pick' ? (
        <>
          <div className="create-link-picker-tabs" role="tablist" aria-label="Resource type">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={clsx(
                  'create-link-picker-tab',
                  tab === t.id && 'create-link-picker-tab--active',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="create-link-picker-body gt-keyboard-scroll">
            {tab === 'explore' ? (
              <div className="flex flex-col gap-4">
                <div className="relative">
                  <label className="relative block">
                    <span className="sr-only">Search places</span>
                    <SearchIcon
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                      sx={{ fontSize: 20 }}
                      aria-hidden
                    />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => handleSearchQueryChange(e.target.value)}
                      placeholder={CREATE_LINK_COPY.searchPlaceholder}
                      className="w-full rounded-input border border-gray-200 bg-white py-2.5 pl-10 pr-11 font-manrope text-body tracking-body text-gray-900 outline-none focus:border-gray-900"
                      autoComplete="off"
                      aria-label="Search for places"
                    />
                  </label>
                </div>

                {searchError && !isSearching ? (
                  <div className="flex flex-col gap-2 rounded-tag bg-[#fff5f5] p-3">
                    <p className="font-manrope text-footnote text-gray-700">{searchError}</p>
                    <button
                      type="button"
                      onClick={retrySearch}
                      className="w-fit font-manrope text-footnote font-semibold text-orange-500"
                    >
                      Try Again
                    </button>
                  </div>
                ) : null}

                {isSearching ? <PickerLoader label="Searching places…" /> : null}

                {!isSearching && !searchError && searchResults.length > 0 ? (
                  <PlaceSearchResults
                    results={searchResults}
                    onPlaceSelect={(location) => handlePlaceSelected(location)}
                    embedded
                  />
                ) : null}

                {!isSearching &&
                !searchError &&
                searchAttempted &&
                searchQuery.trim().length >= 3 &&
                searchResults.length === 0 ? (
                  <p className="create-link-picker-empty">No places found. Try a different search.</p>
                ) : null}

                {!searchAttempted && isLoading ? (
                  <PickerLoader label="Loading popular places…" />
                ) : null}

                {/* Popular stays until a search has actually been attempted (PlacePicker pattern) */}
                {!searchAttempted && !isLoading && popularPlaces.length > 0 ? (
                  <div>
                    <h3 className="mb-3 font-manrope text-body font-semibold tracking-body text-gray-900">
                      {CREATE_LINK_COPY.popularHeading}
                    </h3>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-2">
                      {popularPlaces.map((place) => (
                        <button
                          key={place._id}
                          type="button"
                          onClick={() => handlePlaceSelected(place)}
                          className="overflow-hidden rounded-card border border-gray-200 bg-white text-left transition-colors hover:bg-gray-100"
                        >
                          <div className="relative aspect-[4/3] w-full bg-gray-100">
                            <CustomImage
                              src={getCreateLinkPlacePhoto(place)}
                              alt=""
                              fill
                              className="object-cover"
                              sizes="(max-width: 768px) 50vw, 240px"
                            />
                          </div>
                          <div className="p-3">
                            <p className="truncate font-manrope text-footnote font-semibold text-gray-900">
                              {place.name}
                            </p>
                            <p className="mt-0.5 truncate font-manrope text-footnote text-gray-500">
                              {getCreateLinkPlaceCity(place)}
                            </p>
                            {getCreateLinkCategoryLabel(place) ? (
                              <span className="mt-2 inline-flex rounded-tag border border-gray-200 px-2 py-0.5 font-manrope text-[11px] font-semibold uppercase tracking-label text-gray-700">
                                {getCreateLinkCategoryLabel(place)}
                              </span>
                            ) : null}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {!searchAttempted && popularPlaces.length === 0 && !isLoading ? (
                  <p className="create-link-picker-empty">
                    Search for any place to create a recommendation link.
                  </p>
                ) : null}
              </div>
            ) : isLoading ? (
              <PickerLoader label="Loading…" />
            ) : visibleListResources.length === 0 ? (
              <p className="create-link-picker-empty">
                No {tabs.find((t) => t.id === tab)?.label.toLowerCase()} available to share yet.
              </p>
            ) : (
              <ul className="create-link-picker-list">
                {visibleListResources.map(({ resource, location }) => {
                  const key = `${resource.resource_type}:${resource.resource_id}`;
                  const busy = isGenerating || isOpeningRecommend;
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handleResourceSelected(resource, location)}
                        className="create-link-picker-item"
                      >
                        <div className="create-link-picker-item-info">
                          <p className="create-link-picker-item-title">{resource.title}</p>
                          {resource.subtitle ? (
                            <p className="create-link-picker-item-subtitle">{resource.subtitle}</p>
                          ) : null}
                        </div>
                        <span className="create-link-picker-item-action">
                          {busy ? 'Creating…' : 'Select'}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      ) : null}

      {step === 'success' && success ? (
        <div className="create-link-picker-body gt-keyboard-scroll flex flex-col gap-4">
          {success.location ? (
            <CreateNewLinkPlaceCard location={success.location} showLiveBadge />
          ) : (
            <div className="rounded-card border border-gray-200 bg-white p-4">
              <p className="font-manrope text-body font-semibold text-gray-900">{success.title}</p>
            </div>
          )}
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => void handleCopyLink()}
              className="w-full rounded-button bg-gray-900 px-4 py-3 font-manrope text-body font-semibold leading-5 tracking-body text-white transition-opacity hover:opacity-90"
            >
              {CREATE_LINK_COPY.copyLink}
            </button>
            <button
              type="button"
              onClick={handleDone}
              className="w-full rounded-button border border-gray-200 bg-white px-4 py-3 font-manrope text-body font-semibold leading-5 tracking-body text-gray-900 transition-colors hover:bg-gray-100"
            >
              {CREATE_LINK_COPY.done}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );

  return (
    <ResponsiveOverlay
      onClose={onClose}
      presentation="sheet"
      forceMobileSheet={isMobileViewport}
      unwrapped={isMobileViewport}
      panelClassName={isMobileViewport ? undefined : 'create-link-picker-panel'}
      showSheetHandle={isMobileViewport}
      aria-label="Create New Link"
    >
      {isMobileViewport ? (
        <div className="create-link-picker-drawer" onClick={(e) => e.stopPropagation()}>
          {flowContent}
        </div>
      ) : (
        <div className="create-link-picker-content" onClick={(e) => e.stopPropagation()}>
          {flowContent}
        </div>
      )}
    </ResponsiveOverlay>
  );
};

export default CreateNewLinkFlow;
